import { useState, useEffect } from "react";
import { Youtube, Instagram, FileText, CheckSquare, Search, SlidersHorizontal, Plus, ArrowDown, ChevronDown } from "lucide-react";
import { SiTiktok } from "react-icons/si";

import { useTranslation } from 'react-i18next';
interface ListRow {
    id: string;
    title: string;
    platform: "youtube" | "instagram" | "tiktok" | "idea";
    stage: string;
    stageColor: string;
    date: string;
    hasChecklist: boolean;
    checkDone: number;
    checkTotal: number;
}

const rows: ListRow[] = [
    { id: "1", title: "Product Launch Video", platform: "youtube", stage: "Idea", stageColor: "bg-yellow-100 text-yellow-800", date: "Mar 15", hasChecklist: true, checkDone: 2, checkTotal: 5 },
    { id: "2", title: "Instagram Reel — Tips", platform: "instagram", stage: "In Progress", stageColor: "bg-blue-100 text-blue-800", date: "Mar 17", hasChecklist: true, checkDone: 4, checkTotal: 4 },
    { id: "3", title: "TikTok Trend Dance", platform: "tiktok", stage: "Review", stageColor: "bg-purple-100 text-purple-800", date: "Mar 18", hasChecklist: false, checkDone: 0, checkTotal: 0 },
    { id: "4", title: "SEO Tutorial Part 2", platform: "youtube", stage: "Published", stageColor: "bg-green-100 text-green-800", date: "Mar 12", hasChecklist: true, checkDone: 6, checkTotal: 6 },
    { id: "5", title: "Behind the Scenes", platform: "instagram", stage: "In Progress", stageColor: "bg-blue-100 text-blue-800", date: "Mar 20", hasChecklist: true, checkDone: 1, checkTotal: 3 },
    { id: "6", title: "Q&A with Followers", platform: "tiktok", stage: "Idea", stageColor: "bg-yellow-100 text-yellow-800", date: "Mar 22", hasChecklist: false, checkDone: 0, checkTotal: 0 },
    { id: "7", title: "Weekly Vlog #24", platform: "youtube", stage: "Published", stageColor: "bg-green-100 text-green-800", date: "Mar 10", hasChecklist: true, checkDone: 5, checkTotal: 5 },
    { id: "8", title: "Brand Collab Post", platform: "instagram", stage: "Review", stageColor: "bg-purple-100 text-purple-800", date: "Mar 21", hasChecklist: true, checkDone: 3, checkTotal: 4 },
];

const platformIcons: Record<string, { icon: React.ReactNode; bg: string }> = {
    youtube: { icon: <Youtube className="size-3 text-red-600" />, bg: "bg-red-100" },
    instagram: { icon: <Instagram className="size-3 text-pink-600" />, bg: "bg-pink-100" },
    tiktok: { icon: <SiTiktok className="size-3 text-white" />, bg: "bg-gray-800" },
    idea: { icon: <FileText className="size-3 text-gray-500" />, bg: "bg-gray-100" },
};

export const AnimatedListPreview = () => {
    const { t } = useTranslation();
    const [visibleRows, setVisibleRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [showBulkBar, setShowBulkBar] = useState(false);
    const [showToolbar, setShowToolbar] = useState(false);
    const [searchTyping, setSearchTyping] = useState("");
    const [showFilterHighlight, setShowFilterHighlight] = useState(false);

    const searchText = "Product Launch";

    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];

        const runAnimation = () => {
            // Phase 1: Show toolbar with slide-down
            timers.push(setTimeout(() => setShowToolbar(true), 200));

            // Phase 2: Type in search box letter by letter
            for (let i = 0; i <= searchText.length; i++) {
                timers.push(
                    setTimeout(() => setSearchTyping(searchText.slice(0, i)), 600 + i * 80)
                );
            }

            // Phase 3: Animate rows appearing
            const searchDone = 600 + searchText.length * 80 + 300;
            rows.forEach((_, i) => {
                timers.push(setTimeout(() => setVisibleRows(i + 1), searchDone + i * 150));
            });

            // Phase 4: Clear search, show filter highlight
            const rowsDone = searchDone + rows.length * 150 + 600;
            timers.push(setTimeout(() => {
                setSearchTyping("");
                setShowFilterHighlight(true);
            }, rowsDone));

            // Phase 5: Select some rows
            timers.push(setTimeout(() => {
                setShowFilterHighlight(false);
                setSelectedRows(["1", "3", "5"]);
            }, rowsDone + 1000));

            // Phase 6: Show bulk action bar
            timers.push(setTimeout(() => setShowBulkBar(true), rowsDone + 1600));

            // Phase 7: Reset
            timers.push(setTimeout(() => {
                setShowBulkBar(false);
                setSelectedRows([]);
                setVisibleRows(0);
                setShowToolbar(false);
                setSearchTyping("");
                setShowFilterHighlight(false);
            }, rowsDone + 4500));
        };

        runAnimation();

        const totalCycle = 600 + searchText.length * 80 + 300 + rows.length * 150 + 600 + 4500 + 1000;
        const interval = setInterval(runAnimation, totalCycle);

        return () => {
            timers.forEach(clearTimeout);
            clearInterval(interval);
        };
    }, []);

    return (
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden h-full">
            {/* macOS Window Bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                <span className="ml-2 text-xs font-semibold text-gray-500">{t('landing.mock.listView')}</span>
                <span className="ml-auto text-[10px] text-gray-400">{rows.length} items</span>
            </div>

            {/* Toolbar / Filter Bar */}
            <div className={`flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all duration-500 ${showToolbar ? "opacity-100 max-h-14" : "opacity-0 max-h-0 overflow-hidden"}`}>
                {/* Search Input */}
                <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
                    <div className="w-full pl-5 pr-2 py-1 text-[9px] bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 truncate" style={{ minHeight: 22 }}>
                        {searchTyping ? (
                            <span>
                                {searchTyping}
                                <span className="inline-block w-px h-2.5 bg-primary-500 ml-px animate-pulse" />
                            </span>
                        ) : (
                            <span className="text-gray-400">{t('landing.mock.searchCards')}</span>
                        )}
                    </div>
                </div>

                {/* All Stages Dropdown */}
                <div className={`flex items-center gap-1 px-2 py-1 text-[8px] font-medium rounded-md border transition-all duration-300 whitespace-nowrap cursor-default ${showFilterHighlight ? "border-primary-400 bg-primary-50 text-primary-700" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}>{t('landing.mock.allStages')}<ChevronDown className="size-2" />
                </div>

                {/* Sort by Date Dropdown */}
                <div className="flex items-center gap-1 px-2 py-1 text-[8px] font-medium bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-600 dark:text-gray-400 whitespace-nowrap cursor-default">{t('landing.mock.sortByDate')}<ChevronDown className="size-2" />
                </div>

                {/* Sort Order */}
                <div className="flex items-center gap-0.5 px-2 py-1 text-[8px] font-medium bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-600 dark:text-gray-400 whitespace-nowrap cursor-default">
                    <ArrowDown className="size-2" />{t('landing.mock.desc')}</div>

                {/* Filters Button */}
                <div className={`flex items-center gap-0.5 px-2 py-1 text-[8px] font-medium rounded-md border transition-all duration-300 whitespace-nowrap cursor-default ${showFilterHighlight ? "bg-primary-600 text-white border-primary-600" : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400"}`}>
                    <SlidersHorizontal className="size-2" />{t('landing.mock.filters')}</div>

                {/* Card Count */}
                <span className="text-[8px] text-gray-400 whitespace-nowrap">{rows.length} cards</span>

                {/* New Card Button */}
                <div className="flex items-center gap-0.5 px-2 py-1 text-[8px] font-bold bg-primary-600 text-white rounded-md whitespace-nowrap cursor-default">
                    <Plus className="size-2.5" />{t('landing.mock.newCard')}</div>
            </div>

            {/* Header Row */}
            <div className="grid grid-cols-[28px_1fr_60px_70px_55px] gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-center">
                    <div className="w-3 h-3 rounded border-2 border-gray-300 dark:border-gray-600" />
                </div>
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mock.title')}</span>
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mock.platform')}</span>
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mock.stage')}</span>
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mock.date')}</span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row, i) => {
                    const isVisible = i < visibleRows;
                    const isSelected = selectedRows.includes(row.id);
                    const plat = platformIcons[row.platform];
                    if (!plat) return null;

                    return (
                        <div
                            key={row.id}
                            className={`grid grid-cols-[28px_1fr_60px_70px_55px] gap-2 px-3 py-2 transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                                } ${isSelected ? "bg-primary-50 dark:bg-primary-900/20" : "hover:bg-gray-50 dark:hover:bg-gray-800/40"}`}
                        >
                            {/* Checkbox */}
                            <div className="flex items-center justify-center">
                                <div
                                    className={`w-3.5 h-3.5 rounded border-2 transition-all duration-300 flex items-center justify-center ${isSelected
                                        ? "bg-primary-500 border-primary-500"
                                        : "border-gray-300 dark:border-gray-600"
                                        }`}
                                >
                                    {isSelected && (
                                        <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            </div>

                            {/* Title */}
                            <div className="flex items-center gap-1.5 min-w-0">
                                <p className="text-[10px] font-medium text-gray-800 dark:text-gray-200 truncate">{row.title}</p>
                                {row.hasChecklist && (
                                    <span className="flex items-center gap-0.5 text-[8px] text-gray-400 flex-shrink-0">
                                        <CheckSquare className="size-2.5" />
                                        {row.checkDone}/{row.checkTotal}
                                    </span>
                                )}
                            </div>

                            {/* Platform */}
                            <div className="flex items-center">
                                <div className={`p-1 rounded ${plat.bg}`}>{plat.icon}</div>
                            </div>

                            {/* Stage */}
                            <div className="flex items-center">
                                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md ${row.stageColor}`}>{row.stage}</span>
                            </div>

                            {/* Date */}
                            <div className="flex items-center">
                                <span className="text-[9px] text-gray-500">{row.date}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bulk Action Bar */}
            <div
                className={`absolute bottom-3 left-1/2 -translate-x-1/2 bg-gray-800 dark:bg-gray-700 text-white rounded-xl px-4 py-2 flex items-center gap-3 shadow-xl shadow-gray-900/30 transition-all duration-500 ${showBulkBar ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                    }`}
            >
                <span className="text-[10px] font-bold">3 selected</span>
                <div className="w-px h-4 bg-white/30" />
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-blue-600/80">{t('landing.mock.move')}</span>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-green-600/80">{t('landing.mock.duplicate')}</span>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-red-600/80">{t('landing.mock.delete')}</span>
            </div>
        </div>
    );
};
